import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { retainSelection } from './selectionPrune';

interface UseSelectionOptions {
  /** Current list of item IDs in display order */
  itemIds: string[];
  /** Used to auto-clear selection when navigating folders */
  currentFolderId: string | null;
  /**
   * Full universe of ids that stay valid to keep selected even when not currently loaded
   * (e.g. after a folder-wide "select all" that materialized unloaded ids). When set, the
   * stale-id pruning keeps any selected id present in EITHER the loaded items or this set,
   * so unloaded-but-folder-valid ids survive a "Load more". Null = prune against loaded only.
   */
  selectableIds?: Set<string> | null;
}

interface UseSelectionReturn {
  selectedIds: Set<string>;
  selectionCount: number;
  hasSelection: boolean;
  isSelected: (id: string) => boolean;
  toggle: (id: string, event?: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }) => void;
  selectAll: () => void;
  selectAllIds: (ids: string[]) => void;
  clearSelection: () => void;
}

export function useSelection({
  itemIds,
  currentFolderId,
  selectableIds = null,
}: UseSelectionOptions): UseSelectionReturn {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const lastClickedRef = useRef<string | null>(null);
  const prevFolderIdRef = useRef(currentFolderId);

  // Clear selection when navigating to a different folder
  useEffect(() => {
    if (prevFolderIdRef.current !== currentFolderId) {
      setSelectedIds(new Set());
      lastClickedRef.current = null;
      prevFolderIdRef.current = currentFolderId;
    }
  }, [currentFolderId]);

  // Prune stale IDs when items change. Keep ids that are still loaded OR known-valid via
  // selectableIds (a folder-wide select-all), so an unloaded selection survives "Load more".
  useEffect(() => {
    const currentIds = new Set(itemIds);
    setSelectedIds((prev) => retainSelection(prev, currentIds, selectableIds));
  }, [itemIds, selectableIds]);

  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    lastClickedRef.current = null;
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(itemIds));
  }, [itemIds]);

  // Select an explicit id list (e.g. all ids in the folder fetched for "Select all").
  // The caller must also supply those ids as `selectableIds` so they survive pruning.
  const selectAllIds = useCallback((ids: string[]) => {
    setSelectedIds(new Set(ids));
  }, []);

  const toggle = useCallback(
    (id: string, event?: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }) => {
      const isMeta = event?.metaKey || event?.ctrlKey;
      const isShift = event?.shiftKey;

      if (isShift && lastClickedRef.current) {
        // Range selection from anchor to target
        const anchorIdx = itemIds.indexOf(lastClickedRef.current);
        const targetIdx = itemIds.indexOf(id);
        if (anchorIdx !== -1 && targetIdx !== -1) {
          const start = Math.min(anchorIdx, targetIdx);
          const end = Math.max(anchorIdx, targetIdx);
          const rangeIds = itemIds.slice(start, end + 1);

          if (isMeta) {
            // Shift+Ctrl/Cmd: add range to existing selection
            setSelectedIds((prev) => {
              const next = new Set(prev);
              for (const rid of rangeIds) next.add(rid);
              return next;
            });
          } else {
            // Shift only: replace selection with range
            setSelectedIds(new Set(rangeIds));
          }
        }
        // Don't update anchor on shift-click
      } else {
        // Toggle single item (add or remove)
        setSelectedIds((prev) => {
          const next = new Set(prev);
          if (next.has(id)) {
            next.delete(id);
          } else {
            next.add(id);
          }
          return next;
        });
        lastClickedRef.current = id;
      }
    },
    [itemIds],
  );

  const selectionCount = selectedIds.size;
  const hasSelection = useMemo(() => selectedIds.size > 0, [selectedIds]);

  return {
    selectedIds,
    selectionCount,
    hasSelection,
    isSelected,
    toggle,
    selectAll,
    selectAllIds,
    clearSelection,
  };
}
